import {
  COMPANION_PAGE_FILTER_KEYS,
  COMPANION_PANEL_FILTER_VALUE_MAX_LENGTH,
  type CompanionPageId,
} from '@fluenceur/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { companionPageForPath, otherPageNameKey } from './companion-page';

/**
 * Written out in full so the i18n orphan check can see every key. An `other`
 * page is named only when the sidebar names it (`otherPageNameKey`); otherwise
 * the composer shows no page chip rather than a vague one.
 */
const PAGE_NAME_KEYS: Record<Exclude<CompanionPageId, 'other'>, string> = {
  dashboard: 'companion.pages.dashboard',
  collabs_tracking: 'companion.pages.collabs_tracking',
  copublicity: 'companion.pages.copublicity',
  ugc: 'companion.pages.ugc',
  brief_templates: 'companion.pages.brief_templates',
  applications: 'companion.pages.applications',
  gifting: 'companion.pages.gifting',
  affiliates: 'companion.pages.affiliates',
  programs: 'companion.pages.programs',
  mentions: 'companion.pages.mentions',
  discovery: 'companion.pages.discovery',
  flows: 'companion.pages.flows',
};

export interface CompanionPage {
  id: CompanionPageId;
  /** The page's name as the navigation says it, or '' for a page it does not name. */
  name: string;
  /** Only the search params the page allows, each capped as the backend expects. */
  filters: Record<string, string>;
}

/** The page the Companion is reading right now, with the filters it may hand along. */
export function useCompanionPage(): CompanionPage {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();

  return useMemo(() => {
    const id = companionPageForPath(pathname);
    const params = new URLSearchParams(search);
    const filters: Record<string, string> = {};
    for (const key of COMPANION_PAGE_FILTER_KEYS[id]) {
      const value = params.get(key);
      if (value) filters[key] = value.slice(0, COMPANION_PANEL_FILTER_VALUE_MAX_LENGTH);
    }
    const nameKey = id === 'other' ? otherPageNameKey(pathname) : PAGE_NAME_KEYS[id];
    return { id, name: nameKey ? t(nameKey) : '', filters };
  }, [pathname, search, t]);
}
